// Implements: F-UIX-020
/**
 * The live preview's tokens: what the server's compiler would emit for the values being edited,
 * as custom properties the preview reads (`--pv-*`).
 *
 * The stacks, radii and spacing units are copies of the compiler's closed tables
 * (`font_stack.py`, `radius.py`, `density.py`): the preview is a sketch of what a profile will
 * look like, never a second source of truth, and the server compiles the real tokens. A value the
 * tables do not know (a half-typed colour, a stack that drifted) is simply left out, and the
 * preview falls back to its neutral instead of drawing a broken value.
 */

import { parseHexColor } from "../../../shared/ui/contrast";
import type { ProfileVersionValues } from "../../../types/design";
import { GOOGLE_FONTS, GOOGLE_FONT_STACKS, PALETTE_ROLES } from "../model/profileModel";

const SYSTEM_STACK_CSS = {
  "system-sans": 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  "system-serif": 'Georgia, Cambria, "Times New Roman", Times, serif',
  "system-rounded": 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Nunito, sans-serif',
  "system-mono": 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
} as const;

/** A Google family falls back to the system stack of its category, as the server's `FontStack.css`. */
const CATEGORY_TAIL = {
  sans: SYSTEM_STACK_CSS["system-sans"],
  serif: SYSTEM_STACK_CSS["system-serif"],
  mono: SYSTEM_STACK_CSS["system-mono"],
} as const;

export const FONT_STACK_CSS: Readonly<Record<string, string>> = {
  ...SYSTEM_STACK_CSS,
  ...Object.fromEntries(
    GOOGLE_FONT_STACKS.map((font) => {
      const { family, category } = GOOGLE_FONTS[font];
      return [font, `"${family}", ${CATEGORY_TAIL[category]}`];
    }),
  ),
};

export const RADIUS_PX: Readonly<Record<string, string>> = {
  none: "0",
  small: "4px",
  medium: "8px",
  rounded: "12px",
  large: "16px",
};

export const DENSITY_PX: Readonly<Record<string, string>> = {
  compact: "4px",
  regular: "8px",
  comfortable: "12px",
};

/** The preview's custom properties for the values being edited; only the ones that are valid. */
export function previewVars(values: ProfileVersionValues): Readonly<Record<string, string>> {
  const vars: Record<string, string> = {};
  for (const role of PALETTE_ROLES) {
    const colour = values.palette[role];
    if (parseHexColor(colour) !== null) vars[`--pv-${role}`] = colour;
  }
  const heading = FONT_STACK_CSS[values.heading_font];
  if (heading !== undefined) vars["--pv-heading-font"] = heading;
  const body = FONT_STACK_CSS[values.body_font];
  if (body !== undefined) vars["--pv-body-font"] = body;
  const radius = RADIUS_PX[values.radius];
  if (radius !== undefined) vars["--pv-radius"] = radius;
  const space = DENSITY_PX[values.density];
  if (space !== undefined) vars["--pv-space"] = space;
  vars["--pv-body-size"] = `${String(values.base_size)}px`;
  vars["--pv-heading-size"] = `${String(Math.round(values.base_size * values.scale ** 2))}px`;
  return vars;
}

function hex(channel: number): string {
  return channel.toString(16).padStart(2, "0");
}

/**
 * The six-digit colour a native colour picker needs for what is typed: a three-digit colour
 * expanded, a six-digit one in lower case, and black for text that is not a colour yet.
 */
export function pickerValue(text: string): string {
  const [red, green, blue] = parseHexColor(text) ?? [0, 0, 0];
  return `#${hex(red)}${hex(green)}${hex(blue)}`;
}
